import { getCurrentOrganization } from '@/lib/utils.ts'
import { api, queryClient } from '@/lib/api-client.ts'
import { invalidateStageGuides } from '@/help/guide/stageGuideActions.ts'
import { AxiosError, AxiosResponse } from 'axios'
import { Organization, useUserStore } from '@/store/user.ts'
import { components } from '@/lib/types/api-types.ts'
import { useOrganizationStore } from '@/routes/settings/organization/users/store/organization.ts'

export const getOrganization = async () => {
  const organizationId = getCurrentOrganization()?.organization?.id
  if (!organizationId) {
    return null
  }
  try {
    const response = await api.get(`/organizations/${organizationId}`)
    return response.data as Organization
  } catch (error) {
    if (error instanceof AxiosError) {
      return null
    }
  }
  return null
}

export const getInvitedOrganizations = async () => {
  const { setInvitedOrganizations } = useOrganizationStore.getState()
  const organizationId = getCurrentOrganization()?.organization?.id
  if (!organizationId) {
    return null
  }
  try {
    const response = await api.get(`/organizations/${organizationId}/invited_organizations`)
    if (response.data && getCurrentOrganization()?.organization?.id === organizationId) {
      setInvitedOrganizations(response.data)
    }
    return response.data || null
  } catch (error) {
    if (error instanceof AxiosError) {
      return null
    }
  }
  return null
}

export const getInvitingOrganizations = async () => {
  const { setInvitingOrganizations } = useOrganizationStore.getState()
  const organizationId = getCurrentOrganization()?.organization?.id
  if (!organizationId) {
    return null
  }
  try {
    const response = await api.get(`/organizations/${organizationId}/inviting_organizations`)
    if (response.data && getCurrentOrganization()?.organization?.id === organizationId) {
      setInvitingOrganizations(response.data)
    }
    return response.data || null
  } catch (error) {
    if (error instanceof AxiosError) {
      return null
    }
  }
  return null
}

export const getOrganizationRoles = async () => {
  try {
    const response = await api.get(`/organization_roles`)
    return response.data
  } catch (error) {
    if (error instanceof AxiosError) {
      return null
    }
  }
  return null
}

export const getCombinedOrganizationData = async () => {
  const { setCurrentOrganization, setOrganizationRoles } = useUserStore.getState()
  const data: Organization | null = await getOrganization()
  const organizationRoles = await getOrganizationRoles()
  if (organizationRoles) {
    setOrganizationRoles(organizationRoles)
  }
  if (data) {
    setCurrentOrganization(data)
    return data
  }
  return null
}

export const deleteOrganizationMember = async (
  memberId: number | undefined,
): Promise<AxiosResponse> => {
  if (!memberId) {
    return Promise.reject('No member id provided')
  }
  return api.delete(`/organization_members/${memberId}`)
}

type EditMemberData = {
  member: string
  organization: string
  role: string
}
export const editOrganizationMember = async (
  memberId: number | undefined,
  formData: EditMemberData,
): Promise<AxiosResponse> => {
  if (!memberId) {
    return Promise.reject('No member id provided')
  }
  return api.patch(`/organization_members/${memberId}`, formData)
}

export type PlaceMemberOnStageData = {
  /** IRI of the participation this company already has on the stage. */
  participation: string
  /** IRI of the person to place. */
  participant: string
  role: 'admin' | 'participant'
}

/**
 * Adding somebody to the company grants organization membership and nothing
 * else — `POST /users` creates no participation at all, so the person lands on
 * no stage and opens an empty app. This is the second half of "add a user":
 * putting them somewhere they can actually work.
 */
export const placeMemberOnStage = async (
  formData: PlaceMemberOnStageData,
): Promise<AxiosResponse> => {
  const response = await api.post(`/project_participants`, formData)
  // Being on the stage is what completes the guide's `inviteTeam` step; `POST /users` alone does
  // not. Not queueable (`queueBypass.ts`), so this is the server's answer, not a mock 202.
  void invalidateStageGuides(queryClient)
  return response
}

/**
 * Send this membership's account invitation again.
 *
 * Re-inviting the address is not an alternative: the person is already a member
 * of this organization, so `POST /users` refuses it. The body is empty because
 * the operation is a command on an existing resource and takes no field — the
 * server owns the token and the address.
 */
export const resendMemberInvite = async (memberId: number | undefined): Promise<AxiosResponse> => {
  if (!memberId) {
    return Promise.reject('No member id provided')
  }
  return api.post(`/organization_members/${memberId}/resend_invite`, {})
}

export type NewMemberData = components['schemas']['User.jsonld-user.post']
export const newOrganizationMember = async (formData: NewMemberData): Promise<AxiosResponse> => {
  return api.post(`/users`, formData)
}
